import { useId } from "react";
import { compute } from "../calc";
import { compare as copy, profilePanel, sections, tabs, FUEL_NOUN } from "../content";
import { useFmt } from "../fmt";
import type { Profile, Vehicle } from "../types";

interface Props {
  vehicles: Vehicle[];
  profiles: Profile[];
  colorOf: (index: number) => string;
}

/** Below this there is only one combination to look at, so there is nothing to compare. */
const MIN_COMBINATIONS = 2;

export function Compare({ vehicles, profiles, colorOf }: Props) {
  const fmt = useFmt();
  const headingId = useId();
  if (vehicles.length * profiles.length < MIN_COMBINATIONS) return null;

  const groups = profiles.map((p) => ({
    p,
    rows: vehicles
      .map((v, i) => ({ v, r: compute(v, p, fmt.unit), color: colorOf(i) }))
      .sort((a, b) => a.r.totalPpm - b.r.totalPpm),
  }));
  const maxPpm = Math.max(1, ...groups.flatMap((g) => g.rows.map((x) => x.r.totalPpm)));
  const scale = 100 / maxPpm;
  // One unit for every figure here: if the dearest total needs £/€, they all use it.
  const major = fmt.isMajor(maxPpm);

  return (
    <section className="compare" aria-labelledby={headingId}>
      <h2 className="eyebrow" id={headingId}>
        {sections.compare}
      </h2>
      <p className="sub">{copy.intro}</p>
      {groups.map((g) => {
        const name = g.p.name || profilePanel.fallbackName;
        return (
          <article className="cmp-group" key={g.p.id}>
            <div className="cmp-head">
              <h3 className="ellipsis" title={g.p.name}>
                {name}
              </h3>
              <span className="cmp-meta">{fmt.profileMeta(g.p)}</span>
            </div>
            {/* Ordered: the list is sorted cheapest first, and that order is the point. */}
            <ol className="compare-rows" aria-label={copy.groupLabel(name)}>
              {g.rows.map(({ v, r, color }, i) => (
                <li className="compare-row" key={v.id}>
                  <div className="cr-name">
                    <span className="cr-line">
                      <span className="fuel-dot" style={{ background: color }} aria-hidden="true" />
                      <span className="ellipsis" title={v.name}>
                        {v.name || tabs.untitled}
                      </span>
                      {i === 0 && g.rows.length > 1 && <span className="cheapest">{copy.cheapest}</span>}
                    </span>
                    <span className="cr-detail">
                      {copy.fixedPrefix} {fmt.perAs(r.fixedPpm, major)} · {FUEL_NOUN[v.fuel]}{" "}
                      {fmt.perAs(r.energyPpm, major)}
                    </span>
                  </div>
                  <div className="cr-bar" aria-hidden="true">
                    {/* One fill sized to the total, split by flex-grow so the segments always add up exactly. */}
                    <div className="cr-fill" style={{ width: `${r.totalPpm * scale}%` }}>
                      {r.fixedPpm > 0 && <div style={{ flexGrow: r.fixedPpm, background: "var(--fixed)" }} />}
                      {r.homeContribution > 0 && (
                        <div style={{ flexGrow: r.homeContribution, background: "var(--home)" }} />
                      )}
                      {r.awayContribution > 0 && (
                        <div style={{ flexGrow: r.awayContribution, background: "var(--away)" }} />
                      )}
                    </div>
                  </div>
                  <div className="cr-total">{fmt.rateAs(r.totalPpm, major)}</div>
                  <div className="cr-annual">{fmt.money(r.annualTotal)}/yr</div>
                </li>
              ))}
            </ol>
          </article>
        );
      })}
    </section>
  );
}
